import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  CompanyListCompanyShortDto,
  CompanyScoringState,
  VerificationStatusDto,
} from 'models/dtoTypes';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Tooltip from '@mui/material/Tooltip';
import {
  GetCompanyListItems,
  LinkCompany,
  TriggerRescoringCompanyList,
  GetCompanyListStatus,
} from 'services/companyList';
import { BulkStatusDto } from 'models/dtoTypes';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchCompanyDialog from './_searchCompanyDialog';
import { ICountryOption } from '.';
import Box from '@mui/material/Box';
import ResourceTable, { IColumn, IResourceTableRef } from 'kosmos/components/ResourceTable';
import { IFilterField } from 'models/types';
import { TFunction, useTranslation } from 'react-i18next';
import BulkStatusToolbar from './_bulkStatusToolbar';
import { BulkCsvDownloadButton } from 'kosmos/components/BulkCsvDownloadButton';
import { CurrentUser } from 'auth';
const PREFIX = 'CompanyListVerificationTable';

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
  pagination: `${PREFIX}-pagination`,
  toolbar: `${PREFIX}-toolbar`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    gap: 16,
    flexDirection: 'row',
  },
  [`& .${classes.pagination}`]: {
    paddingTop: 10,
    paddingBottom: 10,
  },
}));

interface IProps {
  countries: ICountryOption[];
  onErrorResponse: (e: unknown) => void;
  companyListId: string;
  columns: IColumn<CompanyListCompanyShortDto>[];
}

const filterSchema = (t: TFunction): IFilterField[] => {
  return [
    {
      label: t('name'),
      name: 'name',
      widget: 'text',
    },
    {
      label: t('external-identifier'),
      name: 'externalIdentifier',
      widget: 'text',
    },
    {
      label: t('verification-status'),
      name: 'verificationStatus',
      widget: 'select',
      options: [
        { label: t('pending'), alias: VerificationStatusDto.Pending },
        { label: t('not-found'), alias: VerificationStatusDto.NotFound },
        { label: t('verify-error'), alias: VerificationStatusDto.VerifyError },
        {
          label: t('multiple-results'),
          alias: VerificationStatusDto.MultipleResults,
        },
        { label: t('dissolved'), alias: VerificationStatusDto.Dissolved },
        { label: t('verified'), alias: VerificationStatusDto.Verified },
      ],
    },
    {
      label: t('scoring-status'),
      name: 'scoringState',
      widget: 'select',
      options: [
        { label: t('in-progress'), alias: CompanyScoringState.InProgress },
        { label: t('error'), alias: CompanyScoringState.Error },
        { label: t('completed'), alias: CompanyScoringState.Completed },
      ],
    },
  ];
};

export default function CompanyListVerificationTable(props: IProps) {
  const { columns, companyListId, onErrorResponse } = props;
  const isAdmin = CurrentUser.getInstance().isAdmin();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const tableRef = useRef<IResourceTableRef>(null);
  const { t } = useTranslation('default');
  const [selectedCompanyListItem, setSelectedCompanyListItem] =
    React.useState<CompanyListCompanyShortDto>();
  const [scoringSelected, setScoringSelected] = React.useState<boolean>(false);
  const [open, setOpen] = React.useState(false);
  const [bulkStatus, setBulkStatus] = useState<BulkStatusDto>({});
  const filteredColumns = columns.filter((a) => {
    if ((isAdmin && a.label === '') || a.label !== '') return a;
  });

  useEffect(() => {
    fetchBulkStatus();
  }, [companyListId]);

  const fetchBulkStatus = async () => {
    try {
      const status = await GetCompanyListStatus(companyListId);
      setBulkStatus(status);
    } catch (e) {
      onErrorResponse(e);
    }
  };

  const handleCheckboxChange = (id: string, isChecked: boolean) => {
    setSelectedIds((prevSelected) =>
      isChecked ? [...prevSelected, id] : prevSelected.filter((selectedId) => selectedId !== id),
    );
  };

  const canOpenSearchModal = (item: CompanyListCompanyShortDto) => {
    const { id, verificationStatus } = item;
    return Boolean(
      id && verificationStatus && ['NotFound', 'MultipleResults'].includes(verificationStatus),
    );
  };

  const handleSelectRow = (item: CompanyListCompanyShortDto) => {
    const canBeSelected = canOpenSearchModal(item);
    if (canBeSelected) {
      setSelectedCompanyListItem(item);
      setOpen(true);
    }
  };

  React.useEffect(() => {
    if (selectedCompanyListItem) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [selectedCompanyListItem]);

  const handleReScoreSelected = async () => {
    setScoringSelected(true);
    try {
      await TriggerRescoringCompanyList(selectedIds, companyListId);
    } catch (e) {
      onErrorResponse(e);
    }
    setScoringSelected(false);
  };

  const handleRefresh = () => {
    setSelectedIds([]);
    tableRef.current?.loadResources(0);
    fetchBulkStatus();
  };

  const renderReScoreSelectedButton = () => {
    if (!isAdmin) {
      return <></>;
    }
    if (selectedIds.length === 0) {
      const message = 'Please select companies to rescore.';
      return (
        <Tooltip title={message}>
          <Button color="warning" variant="outlined" startIcon={<PlayCircleIcon />}>
            {t('retry')}
          </Button>
        </Tooltip>
      );
    } else {
      return (
        <LoadingButton
          loading={scoringSelected}
          loadingPosition="start"
          color="info"
          onClick={handleReScoreSelected}
          variant="contained"
          startIcon={<PlayCircleIcon />}
        >
          {t('retry')}
        </LoadingButton>
      );
    }
  };

  const renderTopButtons = () => {
    return (
      <div className={classes.toolbar}>
        <div style={{ flex: 1 }}>
          <BulkStatusToolbar bulkStatus={bulkStatus} />
        </div>
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb: 2 }}>
          {renderReScoreSelectedButton()}
          <Button
            color="primary"
            onClick={handleRefresh}
            variant="contained"
            startIcon={<RefreshIcon />}
          >
            {t('refresh')}
          </Button>
        </Stack>
      </div>
    );
  };

  const handleSelectMatchedCompany = async (companyListItemId: string, companyIds: string[]) => {
    try {
      await LinkCompany(companyListId, companyListItemId, companyIds);
      setSelectedCompanyListItem(undefined);
      tableRef.current?.loadResources(0);
    } catch (e) {
      onErrorResponse(e);
    }
  };

  return (
    <Root>
      {renderTopButtons()}
      <ResourceTable<CompanyListCompanyShortDto>
        ref={tableRef}
        stickyHeader={true}
        filterSchema={filterSchema(t)}
        columns={filteredColumns}
        onFetch={(options) => GetCompanyListItems(companyListId, options)}
        onFilterChanged={() => setSelectedIds([])}
        isRowSelectable={canOpenSearchModal}
        onSelectRow={handleSelectRow}
        selectedIds={selectedIds}
        onCheckboxChange={handleCheckboxChange}
        isAdmin={isAdmin}
        defaultSort={{
          sortColumn: 'position',
          isDesc: false,
        }}
      />

      <SearchCompanyDialog
        selectedCompanyListItem={selectedCompanyListItem}
        open={open}
        onClose={() => setSelectedCompanyListItem(undefined)}
        onSelect={handleSelectMatchedCompany}
      />
      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
        <Box sx={{ flex: '1 1 auto' }} />
        <BulkCsvDownloadButton companyListId={companyListId} />
      </Box>
    </Root>
  );
}
